// Spec 45 component-behavior — cmp_ds_avatar.
//
// Person/entity portrait with deterministic fallback when src is
// missing or fails to load. Three render modes:
//
//   * src loaded → <img>
//   * src failed/missing + initials provided → text initials (max 2 chars)
//   * neither → generic "person" silhouette icon
//
// Sizes: 24 / 32 / 40 / 48 / 64 px (closed set per spec 44 icon-grid).
//
// IMPORTANT: alt is REQUIRED. Avatar without an accessible name is
// one of the most common a11y bugs caught in design-system reviews —
// runtime guard surfaces it in dev.
//
// Note: rest props (className, data-*, style, etc.) flow to the root
// <div> wrapper. The inner <img> takes only the props it needs
// (src/alt/width/height/onError); avatar-level data-attrs ride on
// the wrapper so consumer queries (data-testid, etc.) work
// regardless of the resolved render mode.

import { forwardRef, useState, type HTMLAttributes, type SyntheticEvent } from "react";

import { cx } from "../lib/cx.ts";
import { isDevGuardActive } from "../lib/dev-guard.ts";

export type AvatarSize = 24 | 32 | 40 | 48 | 64;

export interface AvatarProps extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  readonly size?: AvatarSize;
  readonly src?: string;
  /** Required accessible name. */
  readonly alt: string;
  /** Optional initials fallback when src fails. Truncated to 2 chars (spec 44 icon-grid budget). */
  readonly initials?: string;
  /** Forwarded to the inner <img> when rendered. Useful for caller-side error logging. */
  readonly onImgError?: (event: SyntheticEvent<HTMLImageElement, Event>) => void;
}

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  ({ size = 40, src, alt, initials, onImgError, className, ...rest }, ref) => {
    if (isDevGuardActive() && (typeof alt !== "string" || alt.trim().length === 0)) {
      throw new Error(
        "<Avatar> requires a non-empty `alt` (avatar without an accessible name fails screenreader users).",
      );
    }

    const [errored, setErrored] = useState(false);
    const showImage = src !== undefined && src.length > 0 && !errored;
    const showInitials = !showImage && initials !== undefined && initials.trim().length > 0;
    const truncatedInitials = (initials ?? "").trim().slice(0, 2).toUpperCase();

    return (
      <div
        ref={ref}
        data-component="avatar"
        data-size={size}
        data-mode={showImage ? "image" : showInitials ? "initials" : "fallback"}
        // Use role=img + aria-label when not showing the actual <img>;
        // otherwise the <img>'s alt provides the name and the wrapper
        // is presentational.
        role={showImage ? undefined : "img"}
        aria-label={showImage ? undefined : alt}
        className={cx("nai-avatar", className)}
        {...rest}
      >
        {showImage && (
          <img
            src={src}
            alt={alt}
            width={size}
            height={size}
            onError={(event) => {
              setErrored(true);
              onImgError?.(event);
            }}
          />
        )}
        {showInitials && (
          <span data-component="avatar-initials" className="nai-avatar-initials">
            {truncatedInitials}
          </span>
        )}
        {!showImage && !showInitials && (
          <svg
            data-component="avatar-fallback"
            className="nai-avatar-fallback"
            width={size}
            height={size}
            viewBox="0 0 40 40"
            aria-hidden
          >
            <circle cx={20} cy={15} r={6} />
            <path d="M8 36 C 8 26, 32 26, 32 36" />
          </svg>
        )}
      </div>
    );
  },
);
Avatar.displayName = "Avatar";
